/**
 * Architect API — typed wrappers + TanStack Query cache keys.
 *
 * Mirrors apps/chaos-gateway/src/routes/architect.ts. Real-time updates
 * land via `useArchitectRunStream` (lib/architect/use-run-stream.ts).
 */
import type {
  ArchitectDispatchAck,
  ArchitectDispatchRequest,
  ArchitectRun,
  ArchitectRunSummary,
} from '@chaos/types/architect';

import { apiJson } from './client';

interface RunListResponse {
  runs: ArchitectRunSummary[];
  total: number;
}
interface RunDetailResponse {
  run: ArchitectRun;
}
interface CardRunResponse {
  run: ArchitectRun | null;
  siblings: Array<{
    id: string;
    title: string;
    boardId: string;
    columnId: string;
    createdAt: string;
  }>;
}

export const architectApi = {
  dispatch(input: ArchitectDispatchRequest): Promise<ArchitectDispatchAck> {
    return apiJson<ArchitectDispatchAck>('/architect/dispatch', {
      method: 'POST',
      json: input,
    });
  },

  listRuns(params?: { limit?: number; offset?: number }): Promise<RunListResponse> {
    const search = new URLSearchParams();
    if (params?.limit !== undefined) search.set('limit', String(params.limit));
    if (params?.offset !== undefined) search.set('offset', String(params.offset));
    const suffix = search.toString();
    return apiJson<RunListResponse>(`/architect/runs${suffix ? `?${suffix}` : ''}`);
  },

  getRun(runId: string): Promise<ArchitectRun> {
    return apiJson<RunDetailResponse>(`/architect/runs/${runId}`).then((r) => r.run);
  },

  cancelRun(runId: string): Promise<{ ok: boolean; status: string }> {
    return apiJson<{ ok: boolean; status: string }>(`/architect/runs/${runId}/cancel`, {
      method: 'POST',
      json: {},
    });
  },

  rerunRun(runId: string): Promise<ArchitectDispatchAck> {
    return apiJson<ArchitectDispatchAck>(`/architect/runs/${runId}/rerun`, {
      method: 'POST',
      json: {},
    });
  },

  getCardRun(cardId: string): Promise<CardRunResponse> {
    return apiJson<CardRunResponse>(`/architect/cards/${cardId}/run`);
  },
};

export const architectKeys = {
  all: ['architect'] as const,
  runs: (params?: { limit?: number; offset?: number }) =>
    [...architectKeys.all, 'runs', params ?? null] as const,
  run: (runId: string) => [...architectKeys.all, 'run', runId] as const,
  cardRun: (cardId: string) => [...architectKeys.all, 'card-run', cardId] as const,
};
